import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { ExternalLink, Link2, Mail, Receipt, X } from 'lucide-react';
import { api } from '../api/client';
import { formatDateTime } from '../lib/format';

// Side drawer showing full BillingEvent detail: raw payload (collapsible),
// linked user, linked parent SALE row (for refunds), email log.
//
// Triggered from the SalesPage row click; closes via the X or backdrop.
export default function BillingEventDrawer({ id, open, onClose }) {
  const query = useQuery({
    queryKey: ['admin-billing-event', id],
    queryFn: () => api.get(`/api/admin/billing-events/${id}`).then((r) => r.data),
    enabled: Boolean(id) && open,
  });

  if (!open) return null;

  const event = query.data?.event;
  const parent = query.data?.parent;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <aside className="ml-auto relative w-full max-w-xl h-full overflow-y-auto bg-white shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="section-icon">
              <Receipt className="w-4 h-4" />
            </span>
            <span className="font-medium">Billing event</span>
          </div>
          <button type="button" className="btn-ghost text-xs" onClick={onClose}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          {query.isLoading && <div className="text-ink-400">Loading…</div>}
          {query.error && (
            <div className="text-rose-600">Failed to load: {query.error.message}</div>
          )}

          {event && (
            <>
              <DetailRow label="Created" value={formatDateTime(event.createdAt)} />
              <DetailRow label="Provider" value={event.provider} />
              <DetailRow label="Event" value={event.eventType} />
              <DetailRow label="Transaction ID" value={event.externalTxnId} mono />
              {event.externalSubscriptionId && (
                <DetailRow label="Subscription ID" value={event.externalSubscriptionId} mono />
              )}
              <DetailRow label="Product ID" value={event.externalProductId} mono />
              {event.externalOfferId && <DetailRow label="Offer ID" value={event.externalOfferId} mono />}
              <DetailRow label="Vendor ID" value={event.externalVendorId || '—'} />
              <DetailRow label="Customer" value={event.customerEmail} />
              {event.customerName && <DetailRow label="Name" value={event.customerName} />}
              {event.internalUserId && (
                <DetailRow
                  label="Internal user"
                  value={
                    <span>
                      {event.internalUserId.email}
                      <span className="text-ink-400 ml-2 text-xs">
                        {event.internalUserId.entitlements
                          ? Object.entries(event.internalUserId.entitlements)
                              .filter(([k, v]) => v === true)
                              .map(([k]) => k)
                              .join(' · ') || 'free'
                          : ''}
                      </span>
                    </span>
                  }
                />
              )}
              <DetailRow label="SKU" value={event.internalSkuSlug || '—'} />
              <DetailRow label="Amount" value={formatCents(event.amountCents, event.currency)} />
              <DetailRow label="Payment method" value={event.paymentMethod || '—'} />
              {event.affiliateExternalUserId && (
                <DetailRow label="Affiliate (external)" value={event.affiliateExternalUserId} />
              )}
              <DetailRow label="Bump?" value={event.bump ? 'Yes' : 'No'} />

              <SectionHeader>Processing</SectionHeader>
              <DetailRow
                label="Status"
                value={
                  event.processingError ? (
                    <span className="text-rose-600">{event.processingError}</span>
                  ) : event.processedAt ? (
                    <span className="text-emerald-700">
                      Processed at {formatDateTime(event.processedAt)}
                    </span>
                  ) : (
                    <span className="text-ink-500">Pending</span>
                  )
                }
              />
              {event.processingError === 'unmapped_sku' && (
                <div className="ml-32 mt-1">
                  <RouterLink
                    to={`/admin/mappings?${new URLSearchParams({
                      provider: event.provider,
                      productId: event.externalProductId || '',
                      offerId: event.externalOfferId || '',
                      label: event.rawPayload?.product?.name || '',
                    }).toString()}`}
                    className="inline-flex items-center gap-1.5 text-xs text-brand-700 hover:underline"
                  >
                    <Link2 className="w-3 h-3" /> Create a mapping for this product, then click
                    Replay
                  </RouterLink>
                </div>
              )}
              {event.appliedEntitlements?.length > 0 && (
                <DetailRow
                  label="Entitlements applied"
                  value={event.appliedEntitlements.join(', ')}
                />
              )}

              {event.purchaseEmail?.templateKey && (
                <>
                  <SectionHeader>Email</SectionHeader>
                  <DetailRow
                    label="Template"
                    value={
                      <span className="inline-flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5" />
                        {event.purchaseEmail.templateKey}
                      </span>
                    }
                  />
                  <DetailRow label="Status" value={event.purchaseEmail.status || '—'} />
                  {event.purchaseEmail.sentAt && (
                    <DetailRow label="Sent at" value={formatDateTime(event.purchaseEmail.sentAt)} />
                  )}
                  {event.purchaseEmail.providerMessageId && (
                    <DetailRow
                      label="Mandrill ID"
                      value={event.purchaseEmail.providerMessageId}
                      mono
                    />
                  )}
                  {event.purchaseEmail.error && (
                    <DetailRow
                      label="Error"
                      value={<span className="text-rose-600">{event.purchaseEmail.error}</span>}
                    />
                  )}
                </>
              )}

              {parent && (
                <>
                  <SectionHeader>Linked parent SALE</SectionHeader>
                  <DetailRow label="Txn" value={parent.externalTxnId} mono />
                  <DetailRow label="SKU" value={parent.internalSkuSlug || '—'} />
                  <DetailRow label="Amount" value={formatCents(parent.amountCents)} />
                  <DetailRow label="When" value={formatDateTime(parent.createdAt)} />
                </>
              )}

              <SectionHeader>Raw payload</SectionHeader>
              <details className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-xs">
                <summary className="cursor-pointer text-ink-500 select-none">
                  Show JSON ({Object.keys(event.rawPayload || {}).length} keys)
                </summary>
                <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all text-ink-700">
                  {JSON.stringify(event.rawPayload, null, 2)}
                </pre>
              </details>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-xs uppercase tracking-wide text-ink-400 w-32 shrink-0">
        {label}
      </span>
      <span className={`text-ink-800 ${mono ? 'font-mono text-xs' : ''} break-all`}>{value}</span>
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <h4 className="text-xs uppercase tracking-wide text-ink-500 pt-3 mt-3 border-t border-gray-100">
      {children}
    </h4>
  );
}

function formatCents(cents, currency = 'USD') {
  const n = (cents || 0) / 100;
  if (currency === 'USD') return `$${n.toFixed(2)}`;
  return `${n.toFixed(2)} ${currency}`;
}
